import { defineMessages } from 'react-intl';

import type { CustomField } from '@atlaskit/editor-common/extensions/field-definitions';

import type { FieldTransformer } from './fieldTypes.ts';

const i18n = defineMessages({
	labelsFieldDescription: {
		id: 'fabric-extension-lib.blog-posts.config-panel.labels-field.description',
		description:
			'Description to be shown below the "Restrict to these labels" field when configuring the Blog Posts macro.',
		defaultMessage:
			'Add a “-” before any label to exclude blogs with that label.  Add a “+” before any label to require that blogs have that label.',
	},
	spacesFieldDescription: {
		id: 'fabric-extension-lib.blog-posts.config-panel.spaces-field.description',
		description:
			'Description to be shown below the "Restrict to these spaces" field when configuring the Blog Posts macro.',
		defaultMessage: 'Add a “-” before any space to exclude blogs in that space.',
	},
});

export interface AttachmentMacroParams {
	labels?: string | string[];
	spaces?: string | string[];
}

const FIELDS_TO_FIX = ['labels', 'spaces'];

export const blogPosts: FieldTransformer<AttachmentMacroParams> = {
	transformFields: async ({ fields, intl }) => {
		return fields.map((field) => {
			let fieldOverrides = {};
			switch (field.name) {
				case 'labels':
					fieldOverrides = {
						// Remove old description which talks about entering comma-separated syntax.
						description: intl.formatMessage(i18n.labelsFieldDescription),
						// Converts the "labels" field to be a multi-select
						// which is not supported by the old macro browser
						type: 'custom',
						isMultiple: true,
						options: {
							resolver: {
								// Supports adding "+" or "-" prefixes to exclude or require results with certain labels.
								type: 'labelWithPlusMinusPrefixes',
							},
						},
					};
					break;
				case 'spaces':
					fieldOverrides = {
						description: intl.formatMessage(i18n.spacesFieldDescription),
						type: 'custom',
						isMultiple: true,
						options: {
							resolver: {
								// Supports special space values like @self
								// as well as adding "-" prefix to exclude results in a space
								type: 'blogRecentlyUpdatedSpaceWithPlusMinusPrefixes',
							},
						},
					};
					break;
				case 'author':
					fieldOverrides = {
						description: '',
					};
					break;
			}

			return {
				...field,
				...fieldOverrides,
			} as CustomField;
		});
	},
	// Custom field receives an array
	transformBefore(params) {
		FIELDS_TO_FIX.forEach((field) => {
			if (params[field] && typeof params[field] === 'string') {
				params[field] = params[field].split(',');
			}
		});
		return params;
	},
	transformAfter(params) {
		FIELDS_TO_FIX.forEach((field) => {
			if (Array.isArray(params[field])) {
				params[field] = params[field].join(',');
			}
		});
		return params;
	},
};
